/** Shared gallery filter helpers (caption search, dates, categories). */

export const SPECIAL_MONTH_KEYWORDS = [
  'születés',
  'születésnap',
  'karácsony',
  'húsvét',
  'farsang',
  'ünnepség',
  'ünnepségek',
  'családi',
  'család',
  'kézműves',
];

export const GALLERY_CATEGORIES = [
  { id: 'all', label: 'Összes emlék', terms: [] },
  { id: 'birthdays', label: 'Születésnapok', terms: ['születés', 'születésnap'] },
  { id: 'christmas', label: 'Karácsony', terms: ['karácsony'] },
  { id: 'crafts', label: 'Kézműves foglalkozás', terms: ['kézműves'] },
  { id: 'celebrations', label: 'Ünnepségek', terms: ['ünnep', 'ünnepség', 'farsang', 'húsvét'] },
];

export const MORE_CATEGORY_OPTIONS = [
  { id: 'farsang', label: 'Farsang', terms: ['farsang'] },
  { id: 'easter', label: 'Húsvét', terms: ['húsvét'] },
];

export const ALL_GALLERY_FILTER_OPTIONS = [...GALLERY_CATEGORIES, ...MORE_CATEGORY_OPTIONS];

export const REMOVED_CATEGORY_IDS = ['family', 'gym'];

const monthFormatter = new Intl.DateTimeFormat('hu-HU', { year: 'numeric', month: 'long' });

/** Accent-insensitive match for Hungarian captions. */
export function normalizeForSearch(text) {
  return (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

export function captionMatchesTerms(item, terms) {
  if (!terms.length) return true;
  const cap = normalizeForSearch(item.caption || '');
  return terms.some((t) => cap.includes(normalizeForSearch(t)));
}

export function itemMatchesSearch(item, query) {
  const q = normalizeForSearch(query.trim());
  if (!q) return true;
  return normalizeForSearch(item.caption || '').includes(q);
}

export function parsePublishedAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return { year: d.getFullYear(), month: d.getMonth() + 1, date: d };
}

export function monthKey(year, month) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

export function parseMonthKey(key) {
  const [y, m] = (key || '').split('-');
  const year = parseInt(y, 10);
  const month = parseInt(m, 10);
  if (!year || !month) return null;
  return { year, month };
}

export function formatMonthKey(key) {
  const p = parseMonthKey(key);
  if (!p) return '';
  return monthFormatter.format(new Date(p.year, p.month - 1, 1));
}

/** Sorted month keys (asc) that have at least one item with publishedAt. */
export function buildMonthSteps(items) {
  const set = new Set();
  for (const item of items) {
    const p = parsePublishedAt(item.publishedAt);
    if (p) set.add(monthKey(p.year, p.month));
  }
  return [...set].sort();
}

export function latestMonthKey(steps) {
  return steps.length ? steps[steps.length - 1] : null;
}

export function itemInMonth(item, key) {
  if (!key) return true;
  const p = parsePublishedAt(item.publishedAt);
  if (!p) return false;
  return monthKey(p.year, p.month) === key;
}

export function monthIsSpecial(items, key) {
  const inMonth = items.filter((i) => itemInMonth(i, key));
  return inMonth.some((i) => captionMatchesTerms(i, SPECIAL_MONTH_KEYWORDS));
}

export function getCategoryById(id) {
  return (
    GALLERY_CATEGORIES.find((c) => c.id === id) ||
    MORE_CATEGORY_OPTIONS.find((c) => c.id === id) ||
    GALLERY_CATEGORIES[0]
  );
}

export function filterByCategory(items, categoryId) {
  const cat = getCategoryById(categoryId);
  if (!cat.terms.length) return items;
  return items.filter((i) => captionMatchesTerms(i, cat.terms));
}

export function hasDisplayableImage(item) {
  return Boolean((item?.imageUrl || '').trim());
}

export function filterGalleryItems(items, { monthKey: mk, categoryId, searchQuery }) {
  let out = items.filter((i) => hasDisplayableImage(i));
  out = out.filter((i) => itemInMonth(i, mk));
  out = filterByCategory(out, categoryId);
  out = out.filter((i) => itemMatchesSearch(i, searchQuery));
  return out;
}

export function yearTicks(minYear, maxYear) {
  const ticks = [];
  for (let y = minYear; y <= maxYear; y += 1) ticks.push(y);
  return ticks;
}

/** Subset of year labels so ticks do not overlap on narrow timelines. */
export function getVisibleYearTicks(minYear, maxYear, maxLabels = 10) {
  const span = maxYear - minYear + 1;
  if (span <= 0) return [minYear];
  if (span <= maxLabels) return yearTicks(minYear, maxYear);

  const step = Math.ceil(span / (maxLabels - 1));
  const ticks = [];
  for (let y = minYear; y <= maxYear; y += step) ticks.push(y);
  if (ticks[ticks.length - 1] !== maxYear) ticks.push(maxYear);
  return ticks;
}
